import { Button } from "components/Button";
import { NavigationMenu } from "components/NavigationMenu";
import { useCycle, useScroll } from "framer-motion";
import { AcfLink, MediaItem, MenuItem } from "graphql";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import flatListToHierarchical from "utilities/flatListToHierarchical";
import { Announcements } from "components/Announcements";
import Logo from "public/logo/logo.svg";
import LogoAlt from "public/logo/logo-alt.svg";
import { Socials } from "components/Socials";

export interface HeaderProps {
  menuItems: MenuItem[];
  logo: MediaItem;
  logoAlt: MediaItem;
  cta: AcfLink;
  pageTitle: string;
  turnOnAnnouncements: boolean;
  announcements?: any[];
  topBorder: boolean;
  social: any;
}

export interface SiteLogoProps {
  isScrolled: boolean;
}

const SiteLogo = ({ isScrolled }: SiteLogoProps) => {
  if (isScrolled) {
    return (
      <Link href="/" aria-label="Home">
        <LogoAlt className={`h-16`} />
      </Link>
    );
  }
  return (
    <Link href="/" aria-label="Home">
      <Logo className={`hidden h-24 lg:flex`} />
      <LogoAlt className={`flex h-16 lg:hidden`} />
    </Link>
  );
};

const Header = ({
  menuItems,
  logo,
  logoAlt,
  cta,
  pageTitle,
  turnOnAnnouncements,
  announcements,
  topBorder,
  social,
}: HeaderProps) => {
  const [open, cycleOpen] = useCycle(false, true);
  const ref = useRef(null);
  const { scrollY } = useScroll({
    target: ref,
    offset: ["start end", "end end"],
  });
  const [isScrolledPast, setIsScrolledPast] = useState(false);

  useEffect(() => {
    return scrollY.on("change", (y) => {
      // If latest is greater than height of header, set isScrolledPast to true
      if (y > 0) {
        setIsScrolledPast(true);
      } else {
        setIsScrolledPast(false);
      }
    });
  }, [scrollY]);

  const borderStyle = topBorder ? "border-b-[20px] border-primary" : "";

  const isTaylors = pageTitle === "taylors";

  let MenuSide = "";
  if (isTaylors) {
    MenuSide = "ml-4";
  } else {
    MenuSide = "mr-4";
  }

  return (
    <header
      className={`${
        isScrolledPast ? `shadow-md` : ``
      } ${isTaylors ? `fixed w-full ${isScrolledPast && `bg-white`}` : `bg-white sticky` } body-font top-0 z-30 max-h-fit text-gray-600 transition-shadow ${borderStyle}`}
      ref={ref}
      id={`header`}
    >
      {turnOnAnnouncements && !isTaylors ? (
        <Announcements announcements={announcements} />
      ) : null}
      <div className={`container mx-auto flex w-full flex-row flex-wrap ${isTaylors ? `justify-start md:justify-end` : `justify-between`} lg:items-center lg:p-4`}>
        {!isTaylors && (
          <div
            className={`flex flex-row flex-wrap items-center justify-center py-2 pl-4 lg:justify-start lg:py-0 lg:pl-0`}
          >
            {/* Logo */}
            <SiteLogo isScrolled={isScrolledPast} />
          </div>
        )}
        <div
          className={`flex flex-row flex-wrap items-center gap-4 lg:w-auto ${isTaylors ? `py-4 w-full justify-between lg:justify-center` : `justify-center`}`}
        >
          {/* Navigation Menu */}
          <NavigationMenu
            menuItems={flatListToHierarchical(menuItems)}
            isOpen={open}
            type="secondary"
            MenuSide={MenuSide}
            toggleOpen={() => cycleOpen()}
            className={`order-last lg:order-1`}
          />
          {cta && !isTaylors ? (
            <Button
              variant={`secondary`}
              href={cta.url ?? ``}
              className={`order-first w-fit min-w-fit lg:order-last`}
              target={cta.target ?? `_self`}
            >
              {cta.title}
            </Button>
          ) : null}
          {isTaylors && (
            <Socials socials={social} type="dark" />
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
